import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { api, useAuth } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Building2, DollarSign, Wallet, TrendingUp, Calendar, 
  CreditCard, CheckCircle, AlertCircle, ArrowUpRight, RefreshCw,
  Banknote, Clock, History, PiggyBank
} from 'lucide-react';
import { toast } from 'sonner';

export default function BusinessOwnerPage() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const [loading, setLoading] = useState(true);
  const [ownerStatus, setOwnerStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [showBankingForm, setShowBankingForm] = useState(false);
  const [showWithdrawForm, setShowWithdrawForm] = useState(false);
  const [bankingForm, setBankingForm] = useState({
    bank_name: '',
    account_holder_name: user?.name || '',
    routing_number: '',
    account_number: '',
    account_type: 'checking'
  });
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkOwnerStatus();
  }, []);

  const checkOwnerStatus = async () => {
    try {
      const res = await api.get('/owner/check');
      setOwnerStatus(res.data);
      
      if (res.data.is_owner) {
        fetchEarnings();
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to check owner status:', error);
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    try {
      const res = await api.get('/owner/earnings');
      setEarnings(res.data);
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
    }
  };

  const handleBankingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.post('/owner/banking', bankingForm);
      toast.success('Banking information saved successfully!');
      setShowBankingForm(false);
      checkOwnerStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save banking information');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (amount < 20) {
      toast.error('Minimum withdrawal is $20');
      return;
    }
    if (amount > (earnings?.earnings?.available || 0)) {
      toast.error('Insufficient available earnings');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/owner/withdraw', { amount });
      toast.success(res.data.message);
      setShowWithdrawForm(false);
      setWithdrawAmount('');
      fetchEarnings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-[#00E676]" />
        </div>
      </DashboardLayout>
    );
  }

  // Not a business owner
  if (!ownerStatus?.is_owner) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto" data-testid="owner-not-found">
          <Card className="card-dashboard border-yellow-500/30">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-16 h-16 mx-auto text-yellow-500 mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Not a Business Owner</h2>
              <p className="text-gray-400 mb-4">
                Your account name "{user?.name}" is not registered as a business owner.
              </p>
              <p className="text-sm text-gray-500">
                If you own a business listed on Sraivest, please contact support to link your account.
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="owner-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <img 
              src={ownerStatus.business_logo} 
              alt={ownerStatus.business_name}
              className="w-16 h-16 rounded-xl object-cover"
            />
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {ownerStatus.business_name}
              </h1>
              <p className="text-gray-400">Business Owner Dashboard</p>
            </div>
          </div>
          <Button 
            onClick={fetchEarnings}
            variant="outline"
            className="border-white/10"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Banking Setup Alert */}
        {!ownerStatus.banking_setup && (
          <Card className="card-dashboard border-[#F7931A]/50 bg-[#F7931A]/10">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-8 h-8 text-[#F7931A]" />
                  <div>
                    <p className="text-white font-semibold">Set Up Banking</p>
                    <p className="text-sm text-gray-400">Add your bank account to receive earnings</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setShowBankingForm(true)}
                  className="bg-[#F7931A] text-black hover:bg-[#F7931A]/80"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add Bank Account
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Earnings Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="card-dashboard border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/10 to-transparent">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Earnings</p>
                  <p className="text-2xl font-bold text-[#00E676]" data-testid="total-earnings">
                    {formatCurrency(earnings?.earnings?.total || ownerStatus.earnings?.total || 0)}
                  </p>
                  <p className="text-xs text-gray-500">90% of all investments</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#00E676]/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#00E676]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Available to Withdraw</p>
                  <p className="text-2xl font-bold text-white" data-testid="available-earnings">
                    {formatCurrency(earnings?.earnings?.available || ownerStatus.earnings?.available || 0)}
                  </p>
                  <p className="text-xs text-gray-500">35% withdrawal fee</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Total Withdrawn</p>
                  <p className="text-2xl font-bold text-white" data-testid="total-withdrawn">
                    {formatCurrency(earnings?.earnings?.withdrawn || ownerStatus.earnings?.withdrawn || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Banking Info & Withdraw */}
        {ownerStatus.banking_setup && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#00E676]" />
                Bank Account
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">{ownerStatus.banking_info?.bank_name}</p>
                    <p className="text-sm text-gray-400">****{ownerStatus.banking_info?.account_last4}</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-[#00E676]" />
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="border-white/10"
                    onClick={() => setShowBankingForm(true)}
                  >
                    Update
                  </Button>
                  <Button 
                    onClick={() => setShowWithdrawForm(true)}
                    className="bg-[#00E676] text-black hover:bg-[#00E676]/80"
                    disabled={(earnings?.earnings?.available || 0) < 20}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Withdraw
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Breakdown */}
        {earnings?.daily_breakdown && earnings.daily_breakdown.length > 0 && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#F7931A]" />
                Daily Earnings (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {earnings.daily_breakdown.map((day) => (
                  <div 
                    key={day._id}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-gray-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{day._id}</p>
                        <p className="text-xs text-gray-500">{day.count} investments</p>
                      </div>
                    </div>
                    <p className="text-[#00E676] font-bold">+{formatCurrency(day.amount)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Transactions */}
        {earnings?.recent_transactions && earnings.recent_transactions.length > 0 && (
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-blue-400" />
                Recent Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {earnings.recent_transactions.slice(0, 10).map((tx, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/5"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded flex items-center justify-center ${
                        tx.type === 'investment_income' ? 'bg-[#00E676]/20' : 'bg-red-500/20'
                      }`}>
                        {tx.type === 'investment_income' ? (
                          <ArrowUpRight className="w-4 h-4 text-[#00E676]" />
                        ) : (
                          <Banknote className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {tx.type === 'investment_income' ? 'Investment Income' : 'Withdrawal'}
                        </p>
                        <p className="text-xs text-gray-500">{tx.timestamp?.slice(0, 16).replace('T', ' ')}</p>
                      </div>
                    </div>
                    <p className={`font-bold ${tx.amount >= 0 ? 'text-[#00E676]' : 'text-red-400'}`}>
                      {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Banking Form Modal */}
        {showBankingForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="card-dashboard w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white">Banking Information</CardTitle>
                <CardDescription>Set up your bank account to receive earnings</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBankingSubmit} className="space-y-4">
                  <div>
                    <Label className="text-gray-400">Bank Name</Label>
                    <Input
                      className="input-dark mt-1"
                      value={bankingForm.bank_name}
                      onChange={(e) => setBankingForm({...bankingForm, bank_name: e.target.value})}
                      placeholder="Chase, Bank of America, etc."
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Account Holder Name</Label>
                    <Input
                      className="input-dark mt-1"
                      value={bankingForm.account_holder_name}
                      onChange={(e) => setBankingForm({...bankingForm, account_holder_name: e.target.value})}
                      placeholder="Full legal name"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Routing Number</Label>
                    <Input
                      className="input-dark mt-1"
                      value={bankingForm.routing_number}
                      onChange={(e) => setBankingForm({...bankingForm, routing_number: e.target.value})}
                      placeholder="9-digit routing number"
                      required
                      maxLength={9}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Account Number</Label>
                    <Input
                      className="input-dark mt-1"
                      value={bankingForm.account_number}
                      onChange={(e) => setBankingForm({...bankingForm, account_number: e.target.value})}
                      placeholder="Account number"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400">Account Type</Label>
                    <select
                      className="w-full mt-1 p-2 rounded-lg bg-white/5 border border-white/10 text-white"
                      value={bankingForm.account_type}
                      onChange={(e) => setBankingForm({...bankingForm, account_type: e.target.value})}
                    >
                      <option value="checking">Checking</option>
                      <option value="savings">Savings</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button 
                      type="button"
                      variant="outline" 
                      className="flex-1 border-white/10"
                      onClick={() => setShowBankingForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit"
                      className="flex-1 bg-[#00E676] text-black hover:bg-[#00E676]/80"
                      disabled={submitting}
                    >
                      {submitting ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Withdraw Form Modal */}
        {showWithdrawForm && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <Card className="card-dashboard w-full max-w-md">
              <CardHeader>
                <CardTitle className="text-white">Withdraw Earnings</CardTitle>
                <CardDescription>Transfer earnings to your bank account (35% fee)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-xl bg-white/5">
                  <p className="text-sm text-gray-400">Available Balance</p>
                  <p className="text-2xl font-bold text-[#00E676]">
                    {formatCurrency(earnings?.earnings?.available || 0)}
                  </p>
                </div>
                
                <div>
                  <Label className="text-gray-400">Withdraw Amount</Label>
                  <div className="relative mt-1">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      type="number"
                      className="input-dark pl-10"
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      placeholder="Enter amount"
                      min="20"
                      max={earnings?.earnings?.available || 0}
                    />
                  </div>
                </div>

                {parseFloat(withdrawAmount) >= 20 && (
                  <div className="p-4 rounded-xl bg-white/5 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Withdraw Amount</span>
                      <span className="text-white">${parseFloat(withdrawAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Platform Fee (35%)</span>
                      <span className="text-[#F7931A]">-${(parseFloat(withdrawAmount) * 0.35).toFixed(2)}</span>
                    </div>
                    <div className="border-t border-white/10 pt-2 flex justify-between">
                      <span className="text-white font-semibold">You'll Receive</span>
                      <span className="text-[#00E676] font-bold">${(parseFloat(withdrawAmount) * 0.65).toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-white/10"
                    onClick={() => { setShowWithdrawForm(false); setWithdrawAmount(''); }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-[#00E676] text-black hover:bg-[#00E676]/80"
                    onClick={handleWithdraw}
                    disabled={submitting || parseFloat(withdrawAmount) < 20}
                  >
                    {submitting ? 'Processing...' : 'Withdraw'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
